/*
 * Copyright (c) Funtip GbR 2017.
 * Simon Oldeboershuis, Perry Prust
 */

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faComments, faEnvelope, faEye, faFutbol, faIdBadge } from '@fortawesome/free-regular-svg-icons';
import {
    faAsterisk,
    faBars,
    faBlog,
    faHome,
    faListOl,
    faMusic,
    faThList,
    faUser,
    faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Button, ButtonGroup, ButtonProps, ButtonToolbar, Col, Collapse, Container, Row } from 'reactstrap';
import {
    myProfileBettingPath,
    myProfilePath,
    myProfilePlaylistPath,
    myUserMailPath,
} from '../../screens/UserProfileScreen';
import { AgbConfirmation } from '../AccountUserApp/AgbConfirmation';
import { feedbackPath } from '../AccountUserApp/UserFeedback';
import { bettingPath } from '../Betting';
import {
    NewAskForMembershipBadge,
    NewInvitationsBadge,
    NewMessagesBadge,
    NotificationStatus,
} from '../BettingApp/BettingProfile/NotificationStatus';
import { liveTickerConferencePath } from '../BettingApp/LiveTickerConference';
import { MyGroupHighscores, MyHighscore, myGroupHighscorePath } from '../BettingApp/MyGroupHighscores';
import { blogPath } from '../BloggingApp/BlogLink';
import { blogListPath } from '../BloggingApp/BlogList';
import { gamesListTodayPath } from '../GamesList';
import { highscoresPath } from '../Highscores';
import { Login } from '../Login';
import { R1MobileMenu } from '../RadioEins/R1MobileMenu';
import { prizesPath } from '../RadioEins/R1Prices';
import { rulesPath } from '../RadioEins/R1Rules';
import { rankingPath } from '../RankingComponent';
import { rankingLoadWithoutProps } from '../RankingComponent/actions';
import { sysAdminWebUsagePath } from '../SysAdminApp/sysAdminPathes';
import { ErrorMessageButton } from '../System/ErrorMessage';
import { isLoggedInSelector, isSysAdminSelector, systemNavLocation, userIdSelector } from '../System/selectors';
import { AppFeature, getStaticFilesBaseUrl, hasFeature, isFuntip, isR1Mobile } from '../System/systemConfig';
import { userGroupHighscorePath, userGroupListPath } from '../UserGroupHighscores';
import { userListPath } from '../UserList';
import { userViewProfilePath } from '../UserView';
import { ShowWhenFuntip } from '../common/ShowWhenFuntip';
import { ShowWhenNotificationStatusMessagesExists } from '../common/ShowWhenNotificationStatusMessagesExist';
import { ShowWhenRadioEins } from '../common/ShowWhenRadioEins';
import { ShowWhenServerAvailable } from '../common/ShowWhenServerAvailable';
import { ShowWhenUserHasRole } from '../common/ShowWhenUserHasRole';
import { MenuUserState } from './MenuUserState';
import { SubMenuLink } from './SubMenuLink';

interface ButtonWithLabelAndIconProps extends ButtonProps {
    label: string;
    icon: IconProp;
}

export const ButtonWithLabelAndIcon = (props: ButtonWithLabelAndIconProps) => {
    return (
        <>
            <Button {...props} className="me-1 d-block d-lg-none d-xl-none">
                <FontAwesomeIcon icon={props.icon} />
                {props.children}
            </Button>
            <Button {...props} className="me-1 d-none d-lg-block d-xl-block">
                {props.label} <FontAwesomeIcon icon={props.icon} />
                {props.children}
            </Button>
        </>
    );
};

interface R1MenuCollopseProps {
    closeMenu: () => void;
}

const R1MenuCollapse = (props: R1MenuCollopseProps) => {
    const isLoggedIn = useSelector(isLoggedInSelector);

    const navLocation = useSelector(systemNavLocation);
    const dispatch = useDispatch();

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const onLiveTicker = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        props.closeMenu();
        // TODO move goto-live-ticker to betting-server
        window.open(
            // workaround for https://github.com/funtip/funtip/issues/1603
            'https://livecenter.sportschau.de/konferenz/',
            // 'https://redaktion.radioeins.de/goto-live-ticker',
            'PopUp',
            'toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=yes,copyhistory=yes,width=356,height=480',
        );
    };

    return (
        <div className="px-4 px-sm-0 pb-0 d-flex flex-row">
            <div className="flex-col col-lg-8 col-sm-10 col-12">
                <div className="row">
                    <div
                        className={`${
                            navLocation === 'betting' ? 'btn-r1-menu-active' : ''
                        } col-12 col-sm-3 text-nowrap p-0 pe-sm-4`}
                    >
                        <div className="border-sm-top border-bottom border-black p-0 py-1 p-sm-2">
                            <Link
                                to={bettingPath('')}
                                title="Tippspiel"
                                className="text-white ps-2"
                                role="btn"
                                onClick={props.closeMenu}
                            >
                                Tippen
                            </Link>
                        </div>
                    </div>
                    <div
                        className={`${
                            navLocation === 'my-highscore' ? 'btn-r1-menu-active' : ''
                        } col-12 col-sm-3 text-nowrap p-0 pe-sm-4`}
                    >
                        <div className="border-sm-top border-bottom border-black p-0 py-1 p-sm-2">
                            <Link
                                to={myGroupHighscorePath()}
                                title="Teamwertung"
                                className="text-white ps-2"
                                onClick={props.closeMenu}
                            >
                                Meine Wertung
                            </Link>
                        </div>
                    </div>
                    <div
                        className={`${
                            navLocation === 'overall-highscore' ? 'btn-r1-menu-active' : ''
                        } col-12 col-sm-3 text-nowrap p-0 pe-sm-4`}
                    >
                        <div className="border-sm-top border-bottom border-black p-0 py-1 p-sm-2">
                            <Link
                                to={highscoresPath('')}
                                title="Tipper"
                                className="text-white ps-2"
                                onClick={props.closeMenu}
                            >
                                Alle Tipper
                            </Link>
                        </div>
                    </div>
                    <div
                        className={`${
                            navLocation === 'profile' ? 'btn-r1-menu-active' : ''
                        } col-12 col-sm-3 text-nowrap p-0 pe-sm-4`}
                    >
                        <div className="border-sm-top border-bottom border-black p-0 py-1 p-sm-2">
                            {isLoggedIn && (
                                <div className="text-white">
                                    <Link
                                        to={myProfilePath()}
                                        title="Mein Profil"
                                        className="text-white ps-2"
                                        onClick={props.closeMenu}
                                    >
                                        Profil
                                    </Link>
                                </div>
                            )}
                            {!isLoggedIn && (
                                <Link to={bettingPath()} className="text-white ps-2">
                                    Anmelden
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div
                        className={`${
                            navLocation === 'ranking' ? 'btn-r1-menu-active' : ''
                        } col-12 col-sm-3 text-nowrap p-0 pe-sm-4`}
                    >
                        <div className="border-bottom border-black p-0 py-1 p-sm-2">
                            <Link
                                to={'#'}
                                color="link"
                                onClick={() => {
                                    props.closeMenu();
                                    dispatch(rankingLoadWithoutProps());
                                }}
                                title="Tabellen"
                                className="text-white ps-2"
                            >
                                Tabellen
                            </Link>
                        </div>
                    </div>
                    <div
                        className={`${
                            navLocation === 'live' ? 'btn-r1-menu-active' : ''
                        } col-12 col-sm-3 text-nowrap p-0 pe-sm-4`}
                    >
                        <div className="border-bottom border-black p-0 py-1 p-sm-2">
                            <Link
                                to={gamesListTodayPath()}
                                title="Live"
                                className="text-white ps-2"
                                onClick={props.closeMenu}
                            >
                                Spiele Live
                            </Link>
                        </div>
                    </div>
                    <div
                        className={`${
                            navLocation === 'rules' || navLocation === 'prizes' ? 'btn-r1-menu-active' : ''
                        } col-12 col-sm-3 text-nowrap p-0 pe-sm-4`}
                    >
                        <div className="border-bottom border-black p-0 py-1 p-sm-2">
                            <Link
                                to={rulesPath()}
                                title="Tippspiel"
                                className="text-white ps-2"
                                onClick={props.closeMenu}
                            >
                                Regeln
                            </Link>
                            <span className="text-white"> /</span>
                            <Link
                                to={prizesPath()}
                                title="Tippspiel"
                                className="text-white ps-2"
                                onClick={props.closeMenu}
                            >
                                Preise
                            </Link>
                        </div>
                    </div>
                    <div className={`col-12 d-none d-sm-block col-sm-3 text-nowrap p-0 pe-sm-4`}>
                        <div
                            className="border-bottom border-black p-0 py-1 p-sm-2"
                            style={{ backgroundColor: 'rgba(70,199,70,0.3)' }}
                        >
                            <Link to={liveTickerConferencePath()} title="Live-Ticker" className="text-white ps-2">
                                Live-Ticker
                            </Link>
                        </div>
                    </div>
                    <div className={`col-12 d-sm-none text-nowrap p-0 pe-sm-4`}>
                        <div className="border-bottom border-black p-0 py-1 p-sm-2">
                            <Link
                                to={liveTickerConferencePath()}
                                title="Live-Ticker"
                                className="text-white ps-2"
                                onClick={(e) => {
                                    props.closeMenu();
                                }}
                            >
                                Live-Ticker
                            </Link>
                        </div>
                    </div>
                    <div className="col-12 d-sm-none pb-4"></div>
                </div>
            </div>
            <div className="flex-col flex-fill"></div>
            <div className="flex-col">
                <div className="d-none d-sm-flex flex-row flex-wrap ">
                    <div className="flex-col py-1 pe-2">
                        <MyHighscore />
                    </div>
                </div>
                <div className="d-none d-sm-flex flex-row flex-wrap ">
                    <div className="flex-col py-1 px-0">
                        <MyGroupHighscores />
                    </div>
                </div>
            </div>
        </div>
    );
};

export const MenuBetting: React.FC<{}> = () => {
    const userId = useSelector(userIdSelector) || '';
    const isLoggedIn = useSelector(isLoggedInSelector);
    const isSysAdmin = useSelector(isSysAdminSelector);

    const dispatch = useDispatch();

    const [isOpen, setIsOpen] = React.useState(false);
    const closeMenu = () => setIsOpen(false);

    const navLocation = useSelector(systemNavLocation);

    if (isFuntip()) {
        return (
            <>
                <>
                    <div
                        className="d-flex flex-row align-items-center justify-content-between ps-2 pe-2"
                        style={{ backgroundColor: 'white', maxWidth: '980px' }}
                    >
                        <div className="my-2 ms-3 ">
                            <img
                                src={getStaticFilesBaseUrl() + '/static-img/ft/logo.png'}
                                alt="Funtip Tippspiel Logo"
                                width={30}
                                className="img-fluid"
                            />
                            <span className="ms-2">
                                <Link to="/" className="text-decoration-none text-reset">
                                    Funtip Tippspiel
                                </Link>
                            </span>
                        </div>
                        <MenuUserState />
                    </div>
                    <div className="d-flex justify-content-start flex-wrap px-3">
                        <SubMenuLink to={bettingPath('')} selected={navLocation === 'betting'}>
                            Tippen
                        </SubMenuLink>
                        <SubMenuLink to={myGroupHighscorePath()} selected={navLocation === 'my-highscore'}>
                            Meine Wertung
                        </SubMenuLink>
                        <SubMenuLink to={highscoresPath()} selected={navLocation === 'overall-highscore'}>
                            Einzelwertung
                        </SubMenuLink>
                        <SubMenuLink
                            to={userGroupHighscorePath('')}
                            selected={navLocation === 'overall-group-highscore'}
                        >
                            Teamwertung
                        </SubMenuLink>
                        <SubMenuLink to={rankingPath('')} selected={navLocation === 'ranking'}>
                            Tabellen
                        </SubMenuLink>
                        <SubMenuLink to={gamesListTodayPath()} selected={navLocation === 'live'}>
                            Spiele Live
                        </SubMenuLink>
                        <SubMenuLink to={liveTickerConferencePath()} selected={navLocation === 'live-ticker'}>
                            Live-Ticker
                        </SubMenuLink>
                        <SubMenuLink to={userListPath()} selected={navLocation === 'user-list'}>
                            Tipper
                        </SubMenuLink>
                        <SubMenuLink to={userGroupListPath()} selected={navLocation === 'usergroup-list'}>
                            Tippteams
                        </SubMenuLink>
                        {isSysAdmin && <SubMenuLink to={sysAdminWebUsagePath()}>SysAdmin</SubMenuLink>}
                        {isSysAdmin && <SubMenuLink to={'/a'}>ApiFootball</SubMenuLink>}
                    </div>
                </>{' '}
                <AgbConfirmation />
            </>
        );
    } else if (isR1Mobile()) {
        return (
            <>
                <R1MobileMenu />
                <div style={{ marginTop: '75px' }} />
                <AgbConfirmation />
            </>
        );
    }
    return (
        <>
            <div className="bg--r1-menu p-0 m-0">
                <div className="ps-4">
                    <Link to="/b/portal" title="Portal" role="button">
                        <img
                            src="https://static2.funtip.de/static-img/r1/@2xlogo_arenatippspiel_small.png"
                            width="980px"
                            alt="arena-logo"
                            className="p-0 d-none d-lg-inline"
                        />
                        <img
                            src="https://static2.funtip.de/static-img/r1/logo_arenatippspiel_720.png"
                            width="627px"
                            alt="arena-logo"
                            className="p-0 pb-2 d-none d-md-inline d-lg-none"
                        />
                        <img
                            src="https://static2.funtip.de/static-img/r1/logo_arenatippspiel_320.png"
                            width="296px"
                            alt="arena-logo"
                            className="p-0 d-inline d-md-none"
                        />
                    </Link>
                </div>
                <div className="p-0 px-sm-2 px-md-4 pb-sm-4">
                    <div className="bg--r1-menu-collapse">
                        <div className="d-block d-sm-none" style={{ backgroundColor: '#085E08' }}>
                            <Button
                                to="/"
                                color="link"
                                onClick={() => setIsOpen(!isOpen)}
                                className="btn btn-link text-white"
                            >
                                <big>TIPPSPIEL-MENU</big>
                            </Button>
                            <Button to="/" color="link" onClick={() => setIsOpen(!isOpen)} className="float-right">
                                <svg
                                    width="14px"
                                    height="7px"
                                    viewBox="0 0 14 7"
                                    version="1.1"
                                    xmlns="http://www.w3.org/2000/svg"
                                    xmlnsXlink="http://www.w3.org/1999/xlink"
                                >
                                    <title>icon_dropdown_arrow</title>
                                    <defs>
                                        <polygon id="path-1" points="7.13773027 0 0 7 14 7"></polygon>
                                        <filter
                                            x="-42.9%"
                                            y="-85.7%"
                                            width="185.7%"
                                            height="271.4%"
                                            filterUnits="objectBoundingBox"
                                            id="filter-2"
                                        >
                                            <feOffset
                                                dx="0"
                                                dy="0"
                                                in="SourceAlpha"
                                                result="shadowOffsetOuter1"
                                            ></feOffset>
                                            <feGaussianBlur
                                                stdDeviation="2"
                                                in="shadowOffsetOuter1"
                                                result="shadowBlurOuter1"
                                            ></feGaussianBlur>
                                            <feColorMatrix
                                                values="0 0 0 0 0   0 0 0 0 0   0 0 0 0 0  0 0 0 1 0"
                                                type="matrix"
                                                in="shadowBlurOuter1"
                                            ></feColorMatrix>
                                        </filter>
                                    </defs>
                                    <g
                                        id="icon_dropdown_arrow"
                                        stroke="none"
                                        strokeWidth="1"
                                        fill="none"
                                        fillRule="evenodd"
                                    >
                                        <g
                                            id="Path-Copy"
                                            transform="translate(7.000000, 3.500000) scale(1, -1) translate(-7.000000, -3.500000) "
                                        >
                                            <use
                                                fill="black"
                                                fillOpacity="1"
                                                filter="url(#filter-2)"
                                                xlinkHref="#path-1"
                                            ></use>
                                            <use fill="#FFFFFF" fillRule="evenodd" xlinkHref="#path-1"></use>
                                        </g>
                                    </g>
                                </svg>
                            </Button>
                        </div>
                        <div className="d-block d-sm-none">
                            <Collapse isOpen={isOpen}>
                                <R1MenuCollapse closeMenu={closeMenu} />
                            </Collapse>
                        </div>
                        <div className="d-none d-sm-block">
                            <R1MenuCollapse closeMenu={closeMenu} />
                        </div>
                    </div>
                </div>
            </div>
            <Container className="p-lg-2 d-none">
                <Row>
                    <ShowWhenRadioEins>
                        {/*                         <Helmet>
                            <script src="https://static2.funtip.de/static-js/radioeins/rbb-v7.4.0.js" />
                            <script src="https://static2.funtip.de/static-js/radioeins/jquery.ui.selectmenu.js" />
                            <script src="https://static2.funtip.de/static-js/radioeins/react/fireready.js" />
                        </Helmet>
 */}{' '}
                    </ShowWhenRadioEins>
                    <ShowWhenFuntip>
                        <Col xs={1}>
                            <img
                                src="https://static2.funtip.de/static-img/ft/logo.jpeg"
                                alt="logo"
                                width="70"
                                className="me-2 img-fluid"
                            />
                        </Col>
                    </ShowWhenFuntip>
                    <Col>
                        <ButtonToolbar className="mb-lg-2">
                            <ShowWhenServerAvailable server="betting">
                                <ButtonGroup className="m-1">
                                    <Link to={gamesListTodayPath()} title="Live">
                                        <ButtonWithLabelAndIcon
                                            label="Live"
                                            icon={faAsterisk}
                                            outline={false}
                                            color="success"
                                        />
                                    </Link>
                                    <Link to="/b/portal" title="Portal">
                                        <ButtonWithLabelAndIcon
                                            label="Portal"
                                            icon={faHome}
                                            outline={false}
                                            color="success"
                                        />
                                    </Link>
                                    <Link to={bettingPath('')} title="Tippspiel">
                                        <ButtonWithLabelAndIcon
                                            label="Tippspiel"
                                            icon={faFutbol}
                                            outline={false}
                                            color="success"
                                        />
                                    </Link>
                                    <Link to={highscoresPath('')} title="Einzelwertung">
                                        <ButtonWithLabelAndIcon
                                            label="Einzelwertung"
                                            icon={faListOl}
                                            outline={false}
                                            color="success"
                                        />
                                    </Link>
                                    <Link to={userGroupHighscorePath('')} title="Teamwertung">
                                        <ButtonWithLabelAndIcon
                                            label="Teamwertung"
                                            icon={faThList}
                                            outline={false}
                                            color="success"
                                        />
                                    </Link>
                                    <Link to={'#'} onClick={() => dispatch(rankingLoadWithoutProps())} title="Tabellen">
                                        <ButtonWithLabelAndIcon
                                            label="Tabellen"
                                            icon={faBars}
                                            outline={false}
                                            color="success"
                                        />
                                    </Link>
                                    <Link to={userListPath()} title="Tipper">
                                        <ButtonWithLabelAndIcon
                                            label="Tipper"
                                            icon={faUser}
                                            outline={false}
                                            color="success"
                                        />
                                    </Link>
                                    <Link to={userGroupListPath()} title="Teams">
                                        <ButtonWithLabelAndIcon
                                            label="Teams"
                                            icon={faUsers}
                                            outline={false}
                                            color="success"
                                        />
                                    </Link>
                                    <MyGroupHighscores />
                                    <MyHighscore />
                                </ButtonGroup>
                            </ShowWhenServerAvailable>
                            {hasFeature(AppFeature.blog) && (
                                <>
                                    <ShowWhenServerAvailable server="image">
                                        <ShowWhenUserHasRole roleName="forumAdmin">
                                            <ButtonGroup className="m-1">
                                                <Link to={blogListPath()} title="Blogs">
                                                    <ButtonWithLabelAndIcon
                                                        label="Blogs"
                                                        icon={faBlog}
                                                        outline={false}
                                                        color="success"
                                                    />
                                                </Link>
                                            </ButtonGroup>
                                        </ShowWhenUserHasRole>
                                    </ShowWhenServerAvailable>
                                    <ShowWhenServerAvailable server="image">
                                        <ButtonGroup className="m-1">
                                            <Link to={blogPath('latest')} title="Union-Blog">
                                                <ButtonWithLabelAndIcon
                                                    label="Union-Blog"
                                                    icon={faBlog}
                                                    outline={false}
                                                    color="danger"
                                                />
                                            </Link>
                                        </ButtonGroup>
                                    </ShowWhenServerAvailable>
                                </>
                            )}

                            <ButtonGroup className="m-1">
                                <ShowWhenServerAvailable server="account">
                                    <Link to={myProfilePath()} title="Mein Profil">
                                        <ButtonWithLabelAndIcon label="Profil" icon={faIdBadge} outline={false} />
                                    </Link>
                                </ShowWhenServerAvailable>
                                <ShowWhenServerAvailable server="betting">
                                    <ShowWhenNotificationStatusMessagesExists>
                                        <Link to={myUserMailPath()} title="Nachrichten">
                                            <ButtonWithLabelAndIcon
                                                label="Nachrichten"
                                                icon={faEnvelope}
                                                outline={false}
                                            >
                                                <NewMessagesBadge />
                                            </ButtonWithLabelAndIcon>
                                        </Link>
                                    </ShowWhenNotificationStatusMessagesExists>
                                </ShowWhenServerAvailable>
                                <NotificationStatus />
                                {hasFeature(AppFeature.playlist) && (
                                    <ShowWhenServerAvailable server="playlist">
                                        <Link to={myProfilePlaylistPath()} title="Meine Musik">
                                            <ButtonWithLabelAndIcon
                                                label="Meine Musik"
                                                icon={faMusic}
                                                outline={false}
                                            />
                                        </Link>
                                    </ShowWhenServerAvailable>
                                )}
                                {isLoggedIn && (
                                    <ShowWhenServerAvailable server="account">
                                        <Link to={userViewProfilePath(userId)} title="Meine Besucheransicht">
                                            <ButtonWithLabelAndIcon
                                                label="Besucheransicht"
                                                icon={faEye}
                                                outline={false}
                                            />
                                        </Link>
                                    </ShowWhenServerAvailable>
                                )}
                                <ShowWhenServerAvailable server="betting">
                                    <Link to={myProfileBettingPath()} title="Meine Tippteams">
                                        <ButtonWithLabelAndIcon label="Meine Tippteams" icon={faUsers} outline={false}>
                                            <NewInvitationsBadge />
                                            <NewAskForMembershipBadge />
                                        </ButtonWithLabelAndIcon>
                                    </Link>
                                </ShowWhenServerAvailable>
                            </ButtonGroup>
                            <ButtonGroup className="m-1">
                                <ShowWhenServerAvailable server="betting">
                                    <Link to={feedbackPath()} title="Feedback">
                                        <ButtonWithLabelAndIcon
                                            label="Feedback"
                                            icon={faComments}
                                            outline={false}
                                            color="warning"
                                        />
                                    </Link>
                                </ShowWhenServerAvailable>

                                <Login />
                                <ErrorMessageButton />
                                <AgbConfirmation />
                            </ButtonGroup>
                        </ButtonToolbar>
                    </Col>
                </Row>
            </Container>
        </>
    );
};
